import {NotFoundError} from '@packages/utils';
import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  type GetCompanyInfoResponse,
  getCompanyInfo,
} from '../../../core/repositories/companiesRepository';
import {
  type GetCompanyDetailResponse,
  type ResultGetCompanyDetail,
  getCompanyDetail,
} from '../../../core/repositories/companyDetailRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {CompanyDetailPage} from '../../../features/company/components/CompanyDetailPage';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  companyDetailResponse?: GetCompanyDetailResponse | undefined;
  company?: GetCompanyInfoResponse | undefined;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  year?: string;
  isPreview: boolean;
  currentUrl: string;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {id} = context.query;
    // ?year=2025 の場合 2025 を取得する
    const year =
      typeof context.query.year === 'string' ? context.query.year : undefined;
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);

    // プレビューモード対応
    const previewCode = String(context.query.preview_code ?? '');

    const currentUrl = `${GSSKT_HOST}/company/${id}`;
    const props: Props = {
      year: year ?? '',
      isPreview:
        typeof context.query.preview_code === 'string' &&
        context.query.preview_code !== '',
      currentUrl,
    };
    // ログイン状態でプレビューコードがある場合はエラー
    if (isValid && previewCode.length > 0) {
      return {
        notFound: true,
      };
    }

    // 未ログイン状態の場合
    if (!isValid) {
      // プレビューモード考慮
      let companyDetailResponse: ResultGetCompanyDetail;
      try {
        companyDetailResponse = await getCompanyDetail({
          companyId: String(id),
          year,
          previewCode,
          webSeminarLimit: 3,
        });
      } catch {
        return {
          notFound: true,
        };
      }

      if (!companyDetailResponse.ok) {
        if (companyDetailResponse.error === NotFoundError) {
          return {
            notFound: true,
          };
        }

        throw new Error(companyDetailResponse.error);
      }

      const idNum = Number(id);
      const company = await getCompanyInfo({
        cookies: context.req.cookies,
        companyId: idNum,
      });
      props.company = company.ok ? company.value : undefined;

      props.companyDetailResponse = companyDetailResponse.ok
        ? companyDetailResponse.value
        : undefined;

      if (!company.ok || !companyDetailResponse.ok) {
        return {
          notFound: true,
        };
      }

      return {
        props,
      };
    }

    // ログイン状態の場合(プレビューコードは考慮しない)
    let companyDetailResponse: ResultGetCompanyDetail;
    try {
      companyDetailResponse = await getCompanyDetail({
        companyId: String(id),
        year,
        previewCode,
        webSeminarLimit: 3,
      });
    } catch {
      return {
        notFound: true,
      };
    }

    if (!companyDetailResponse.ok) {
      if (companyDetailResponse.error === NotFoundError) {
        return {
          notFound: true,
        };
      }

      throw new Error(companyDetailResponse.error);
    }

    const idNum = Number(id);
    const company = await getCompanyInfo({
      cookies: context.req.cookies,
      companyId: idNum,
    });
    props.company = company.ok ? company.value : undefined;

    if (!company.ok || !companyDetailResponse.ok) {
      return {
        notFound: true,
      };
    }

    props.companyDetailResponse = companyDetailResponse.ok
      ? companyDetailResponse.value
      : undefined;

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  companyDetailResponse,
  company,
  year,
  isPreview,
  currentUrl,
}: Props) => {
  return (
    <>
      <Head>
        <title key="title">{`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活情報・採用大学 | 外資就活ドットコム`}</title>
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta
          key="description"
          name="description"
          content={`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活・インターンシップ情報です。${companyDetailResponse?.metaData?.companyName}のエントリーシートやES・体験記、就活説明会・選考会、企業紹介を掲載。トップ企業の内定をめざす就職活動サイト「外資就活ドットコム」`}
        />
        <meta
          key="keywords"
          name="keywords"
          content={`${companyDetailResponse?.metaData?.companyName},${companyDetailResponse?.metaData?.industryCategoryID},${companyDetailResponse?.metaData?.industryName}外資,就活,インターンシップ,新卒採用,会社説明会,就活コラム,インターン,内定,内定大学,掲示板,体験記,採用,ES・体験記${year ? `,${year}年卒` : ''}`}
        />
        <meta
          key="og:title"
          property="og:title"
          content={`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活・インターンシップ情報 | 外資就活ドットコム`}
        />
        <meta
          key="og:image"
          property="og:image"
          content={`https://gaishishukatsu.com/sns_image/company/${companyDetailResponse?.metaData?.companyID}`}
        />
        <meta
          key="og:url"
          property="og:url"
          content={`https://gaishishukatsu.com/company/${companyDetailResponse?.metaData?.companyID}`}
        />
        <meta
          key="og:description"
          property="og:description"
          content={`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活・インターンシップ情報。${companyDetailResponse?.metaData?.companyName}のエントリーシートやES・体験記、就活説明会・選考会、企業紹介を掲載。トップ企業の内定をめざす就職活動サイト「外資就活ドットコム」`}
        />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={`${companyDetailResponse?.metaData?.companyName}の新卒採用・就活・インターンシップ情報 | 外資就活ドットコム`}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活・インターンシップ情報。${companyDetailResponse?.metaData?.companyName}のエントリーシートやES・体験記、就活説明会・選考会、企業紹介を掲載。トップ企業の内定をめざす就職活動サイト「外資就活ドットコム」`}
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={`https://gaishishukatsu.com/sns_image/company/${companyDetailResponse?.metaData?.companyID}`}
        />
        <meta name="robots" content="index, follow" />
      </Head>
      {isPreview && (
        <div className="fixed top-0 left-0 w-full h-12 bg-black-1 text-white text-center flex items-center justify-center p-32 z-50">
          プレビューモード
        </div>
      )}
      {companyDetailResponse && company && (
        <CompanyDetailPage
          companyDetail={companyDetailResponse}
          companyInfo={company}
          year={year}
          isPreview={isPreview}
        />
      )}
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;

export default Page;
